import {computed, reactive, ref, watch} from "vue";
import Translator from "@/utils/common/Translator.ts";
import type {MenuItem} from "./useQuestionList.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";

export interface QuestionListFilterModel {
  filterType: 'set';
  values: string[];
  exclude: boolean;
}

export interface QuestionListFilterOptions {
  entityType: string;
  colId: string;
  fieldType?: string;
  referenceTo?: string;
  queriedClass?: string;
  initialModel?: QuestionListFilterModel | null;
  getListRequest?: () => namaTypes.AGGridListRequest | undefined;
  onChange: (model: QuestionListFilterModel | null) => void;
}

export function useQuestionListFilter(opts: QuestionListFilterOptions) {
  const mode = ref<'all' | 'subset'>('all');
  const excluded = reactive(new Set<string>());
  const included = reactive(new Set<string>());

  const items = ref<MenuItem[]>([]);
  const isLoading = ref(false);
  const searchText = ref('');
  let searchSeq = 0;
  let searchDebounceTimer: number | undefined;

  restoreFromModel(opts.initialModel);

  const selectAllState = computed<boolean | null>(() => {
    if (mode.value === 'all')
      return excluded.size === 0 ? true : null;
    return included.size === 0 ? false : null;
  });

  function isItemSelected(item: MenuItem): boolean {
    return mode.value === 'all' ? !excluded.has(item.id) : included.has(item.id);
  }

  function toggleAll() {
    if (mode.value === 'all' && excluded.size === 0) {
      mode.value = 'subset';
      included.clear();
    } else {
      mode.value = 'all';
      excluded.clear();
    }
    emitModel();
  }

  function toggleItem(item: MenuItem) {
    const target = mode.value === 'all' ? excluded : included;
    if (target.has(item.id))
      target.delete(item.id);
    else
      target.add(item.id);
    emitModel();
  }

  function buildModel(): QuestionListFilterModel | null {
    const set = mode.value === 'all' ? excluded : included;
    if (set.size === 0)
      return null;
    return {filterType: 'set', values: [...set], exclude: mode.value === 'all'};
  }

  function emitModel() {
    opts.onChange(buildModel());
  }

  function applyExternalModel(model?: QuestionListFilterModel | null) {
    if (model)
      return;
    if (!buildModel())
      return;
    restoreFromModel(null);
    searchText.value = '';
  }

  function restoreFromModel(model?: QuestionListFilterModel | null) {
    excluded.clear();
    included.clear();
    if (model && model.values?.length) {
      mode.value = model.exclude ? 'all' : 'subset';
      const target = model.exclude ? excluded : included;
      model.values.forEach(v => target.add(v));
    } else {
      mode.value = 'all';
    }
  }

  async function loadDistinctValues(query: string = ''): Promise<boolean> {
    const seq = ++searchSeq;
    isLoading.value = true;
    try {
      const results = await AGGridUtils.fetchDistinctColumnValues({
        colId: opts.colId,
        entityType: opts.entityType,
        fieldType: opts.fieldType,
        referenceTo: opts.referenceTo,
        queriedClass: opts.queriedClass,
        searchText: query,
        listRequest: opts.getListRequest?.(),
      });
      if (seq !== searchSeq) return false;
      items.value = results.filter(r => r != null).map(r => {
        const code = r.code ?? '';
        const caption = r['optionCaption'] ?? Translator.getNameForTitle(r) ?? '';
        return {
          id: r.id ?? code,
          code,
          caption,
          value: r,
          searchValue: (code + ' ' + caption).toLowerCase(),
        };
      });
      return true;
    } finally {
      if (seq === searchSeq) isLoading.value = false;
    }
  }

  function applySearchSelection(query: string) {
    excluded.clear();
    included.clear();
    if (query) {
      mode.value = 'subset';
      items.value.forEach(item => included.add(item.id));
    } else {
      mode.value = 'all';
    }
    emitModel();
  }

  watch(searchText, (q) => {
    if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer);
    const query = (q ?? '').trim();
    searchDebounceTimer = window.setTimeout(async () => {
      if (await loadDistinctValues(query))
        applySearchSelection(query);
    }, 250);
  });

  return {
    mode,
    items,
    isLoading,
    searchText,
    selectAllState,
    isItemSelected,
    toggleAll,
    toggleItem,
    applyExternalModel,
    loadDistinctValues,
  };
}
